import Chart from "chart.js/auto";
import {isEmpty} from "es-toolkit/compat";

const SCALE_MULTIPLIER = {
  progesterone: 10,
  oestradiol: 0.7,
  fsh: 30,
  lh: 10,
};

const DEFAULT_DATA_STYLE = {
  fill: false,
  borderWidth: 7,
  pointBorderWidth: 1,
  backgroundColor: "#fff",
  borderColor: "#fff",
};

const DEFAULT_LINE_CHART_STYLE = {
  legend: {
    display: true,
    boxWidth: 10,
    boxHeight: 10,
    padding: 15,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "#FFFFFF",
    color: "#FFFFFF",
  },
  axis: {
    xDisplay: true,
    yDisplay: true,
  },
  label: {
    xDisplay: true,
    yDisplay: true,
    x: "DAY",
    y: "nmol/L",
    fontSize: 10,
    color: "#b3b3b3",
  },
  colors: ["#C080E1", "#85E1FE", "#FDB5D2", "#A7C1FF"],
  tension: 0.3,
};

const DEFAULT_DOUGHNUT_CHART_STYLE = {
  borderRadius: 20,
  backgroundChartBorderRadius: 0,
  backgroundChartColor: "lightgrey",
  colorsRequired: "#756dcd",
  backgroundColor: "#ffffff",
  hasBlob: false,
  hasGlow: false,
  circumference: 360,
  rotation: 200,
  animate: false,
  borderWidth: 0,
  borderColor: "#ffffff",
  showBackground: false,
  cutout: "85%",
  rootMargin: "0px 0px -50px 0px",
};

const ORDER = {
  Low: 0,
  Average: 1,
  High: 2,
  Personal: 3,
};

/**
 * @typedef {Object} ChartImageOptions
 * @property {("allHormones" | "formScore" | "progesterone" |  "oestradiol" | "lh" | "fsh")} type - The type of chart to generate
 * @property {Object} data - The chart data object containing curves
 * @property {number} [width=794] - The width of the chart image in pixels
 * @property {number} [height=315] - The height of the chart image in pixels
 */

/**
 * Generates a chart image from the given data
 *
 * @param {ChartImageOptions} options
 * @returns {string}
 */

export const generateChartImageV2 = ({type, data, width = 794, height = 315}) => {
  if (!type || isEmpty(data) || width < 1 || height < 1)
    throw (new Error("Invalid parameters for chart generation"), "");

  if (!["lh", "fsh", "allHormones", "progesterone", "oestradiol", "formScore"].some(chartType => chartType === type)) {
    throw new Error(
      "Invalid chart type. Supported types are: 'lh', 'fsh', 'oestradiol', 'progesterone', 'formScore', and 'allHormones'",
    );
  }

  if (type === "formScore") {
    width = 400;
    height = 400;
  }

  const CHARTS = {
    lh: getLhChartImage,
    fsh: getFshChartImage,
    allHormones: getAllHormonesChartImage,
    progesterone: getProgesteroneChartImage,
    oestradiol: getOestradiolChartImage,
    formScore: getFormScoreChartImage,
  };

  const chartImage = CHARTS[type](data, width, height);

  if (!chartImage) {
    throw new Error(`Failed to generate chart image for type: ${type}`);
  }

  return chartImage;
};

const getAllHormonesChartImage = (data, width, height) => {
  const curves = Object.values(data.curves)
    .flatMap(curve => curve.personalCurve.split(","))
    .map(v => parseFloat(v === "" ? 0 : v));

  const maxPersonalCurveValue = Math.max(...curves);

  const scaleCurveValue = (value, multiplier = 1) => {
    if (!maxPersonalCurveValue) return 0;
    return (value / maxPersonalCurveValue) * 100 * multiplier;
  };

  const progesteroneCurves = data.curves.progesterone.personalCurve
    .split(",")
    .map(v => parseFloat(v))
    .map(v => (v > 0 ? scaleCurveValue(v, SCALE_MULTIPLIER.progesterone) : 0))
    .slice(0, -1);

  const oestradiolCurves = data.curves.oestradiol.personalCurve
    .split(",")
    .map(v => parseFloat(v))
    .map(v => (v > 0 ? scaleCurveValue(v, SCALE_MULTIPLIER.oestradiol) : 0))
    .slice(0, -1);

  const fshCurves = data.curves.fsh.personalCurve
    .split(",")
    .map(v => parseFloat(v))
    .map(v => (v > 0 ? scaleCurveValue(v, SCALE_MULTIPLIER.fsh) : 0))
    .slice(0, -1);

  const lhCurves = data.curves.lh.personalCurve
    .split(",")
    .map(v => parseFloat(v))
    .map(v => (v > 0 ? scaleCurveValue(v, SCALE_MULTIPLIER.lh) : 0))
    .slice(0, -1);

  const report = [
    {
      label: "Progesterone",
      data: progesteroneCurves,
      style: {backgroundColor: "#A7C1FF33", borderColor: "#A7C1FF", borderWidth: 3, fill: true},
    },
    {
      label: "Oestradiol",
      data: oestradiolCurves,
      style: {backgroundColor: "#FDB5D233", borderColor: "#FDB5D2", borderWidth: 3, fill: true},
    },
    {
      label: "FSH",
      data: fshCurves,
      style: {backgroundColor: "#C080E133", borderColor: "#C080E1", borderWidth: 3, fill: true},
    },
    {
      label: "LH",
      data: lhCurves,
      style: {backgroundColor: "#85E1FE33", borderColor: "#85E1FE", borderWidth: 3, fill: true},
    },
  ];

  const xLabel = progesteroneCurves.map((_, index) => {
    if (index == 0 || !(index % 7) || index == progesteroneCurves) {
      return index;
    } else {
      return "";
    }
  });

  const styles = {
    ...DEFAULT_LINE_CHART_STYLE,
    label: {
      xDisplay: true,
      yDisplay: false,
      x: "DAY",
      y: "",
      fontSize: 10,
      color: "#5b5b5b",
    },
    axis: {
      yDisplay: false,
      backgroundColor: "#FFFFFF",
    },
    tension: 0.4,
    backgroundColor: "#FFFFFF",
  };

  const dataset = {
    labels: xLabel,
    datasets: report.map(d => {
      return {
        label: d?.label || "",
        backgroundColor: d?.style?.backgroundColor || "#FFFFFF",
        data: d?.data || [],
        ...DEFAULT_DATA_STYLE,
        ...(d?.style || {}),
      };
    }),
  };

  const options = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      customCanvasBackgroundColor: {
        color: styles.backgroundColor,
      },
      legend: {
        display: styles.legend.display,
        position: styles.legend.position ?? "bottom",
        labels: {
          boxWidth: styles.legend.boxWidth,
          boxHeight: styles.legend.boxHeight,
          padding: styles.legend.padding,
          font: {
            size: styles.legend.fontSize,
            weight: styles.legend.fontWeight,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
        backgroundColor: styles.legend.backgroundColor,
        color: styles.legend.color,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    tension: styles.tension,
    scales: {
      x: {
        display: styles.axis.xDisplay,
        backgroundColor: "#FFFFFF",
        title: {
          display: styles.label.xDisplay,
          text: styles.label.x,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min: 0,
          max: 31,
          stepSize: 7,
          z: 99999,
        },
      },
      y: {
        display: styles.axis.yDisplay,
        backgroundColor: styles.axis.backgroundColor,
        title: {
          display: styles.label.yDisplay,
          text: styles.label.y,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          scaleBeginAtZero: false,
          beginAtZero: false,
          min: 0,
          suggestedMin: 0,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          z: 99999,
        },
      },
    },
  };

  // Create the chart
  const newCanvas = document.createElement("canvas");
  newCanvas.setAttribute("id", "tempCanvas");
  document.body.appendChild(newCanvas);
  document.getElementById("tempCanvas").style.maxWidth = width + "px";
  document.getElementById("tempCanvas").style.maxHeight = height + "px";
  document.getElementById("tempCanvas").style.display = "none";

  const ctx = newCanvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  const newChart = new Chart(ctx, {
    type: "line",
    data: dataset,
    options: options,
  });

  const imageUrl = newChart.toBase64Image();
  newCanvas.remove();

  return imageUrl;
};

const getProgesteroneChartImage = (data, width, height) => {
  if (isEmpty(data?.curves?.progesterone)) {
    throw new Error("No progesterone data available for chart generation");
    return "";
  }

  if (width < 1 || height < 1) {
    throw new Error("Invalid width or height for chart generation");
    return "";
  }

  const progesteroneData = Object.entries(data?.curves?.progesterone || {})
    .map(([key, value]) => {
      const LABELS = {
        bottomCurve: "Low",
        meanCurve: "Average",
        personalCurve: "Personal",
        topCurve: "High",
      };

      const STYLES = {
        bottomCurve: {backgroundColor: "#EF94DD", borderColor: "#EF94DD", borderDash: [5, 5], borderWidth: 1},
        meanCurve: {backgroundColor: "#44b17a", borderColor: "#44b17a", borderWidth: 1},
        personalCurve: {backgroundColor: "#766BCF", borderColor: "#766BCF", borderWidth: 7, pointBorderWidth: 1},
        topCurve: {backgroundColor: "#5BC5F2", borderColor: "#5BC5F2", borderDash: [5, 5], borderWidth: 1},
      };

      return {
        label: LABELS[key],
        data: value
          .split(",")
          .map(v => (v > 0 ? parseFloat(v) : 0))
          .slice(0, -1),
        ...(STYLES[key] || {}),
      };
    })
    .sort((a, b) => ORDER[a.label] - ORDER[b.label]);

  const xLabels = progesteroneData[0].data.map((_, index) => {
    if (index == 0 || !(index % 7)) {
      return index;
    } else {
      return "";
    }
  });

  const styles = {
    ...DEFAULT_LINE_CHART_STYLE,
    label: {
      y: data.results?.[0]?.progesterone?.unit || "nmol/L",
    },
    legend: {
      position: "top",
    },
  };

  const dataset = {
    labels: xLabels,
    datasets: progesteroneData,
  };

  const options = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      customCanvasBackgroundColor: {
        color: styles.backgroundColor,
      },
      legend: {
        display: styles.legend.display,
        position: styles.legend.position ?? "bottom",
        labels: {
          boxWidth: styles.legend.boxWidth,
          boxHeight: styles.legend.boxHeight,
          padding: styles.legend.padding,
          font: {
            size: styles.legend.fontSize,
            weight: styles.legend.fontWeight,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
        backgroundColor: styles.legend.backgroundColor,
        color: styles.legend.color,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    tension: styles.tension,
    scales: {
      x: {
        display: styles.axis.xDisplay,
        backgroundColor: "#FFFFFF",
        title: {
          display: styles.label.xDisplay,
          text: styles.label.x,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min: 0,
          max: 31,
          stepSize: 7,
          z: 99999,
        },
      },
      y: {
        display: styles.axis.yDisplay,
        backgroundColor: styles.axis.backgroundColor,
        title: {
          display: styles.label.yDisplay,
          text: styles.label.y,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          scaleBeginAtZero: false,
          beginAtZero: false,
          min: 0,
          suggestedMin: 0,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          z: 99999,
        },
      },
    },
  };

  // Create the chart
  const newCanvas = document.createElement("canvas");
  newCanvas.setAttribute("id", "tempCanvas");
  document.body.appendChild(newCanvas);
  document.getElementById("tempCanvas").style.maxWidth = width + "px";
  document.getElementById("tempCanvas").style.maxHeight = height + "px";
  document.getElementById("tempCanvas").style.display = "none";

  const ctx = newCanvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  const newChart = new Chart(ctx, {
    type: "line",
    data: dataset,
    options: options,
  });

  const imageUrl = newChart.toBase64Image();
  newCanvas.remove();

  return imageUrl;
};

const getOestradiolChartImage = (data, width, height) => {
  if (isEmpty(data?.curves?.oestradiol)) {
    throw new Error("No oestradiol data available for chart generation");
    return "";
  }

  if (width < 1 || height < 1) {
    throw new Error("Invalid width or height for chart generation");
    return "";
  }

  const oestradiolData = Object.entries(data?.curves?.oestradiol || {})
    .map(([key, value]) => {
      const LABELS = {
        bottomCurve: "Low",
        meanCurve: "Average",
        personalCurve: "Personal",
        topCurve: "High",
      };

      const STYLES = {
        bottomCurve: {backgroundColor: "#EF94DD", borderColor: "#EF94DD", borderDash: [5, 5], borderWidth: 1},
        meanCurve: {backgroundColor: "#44b17a", borderColor: "#44b17a", borderWidth: 1},
        personalCurve: {backgroundColor: "#766BCF", borderColor: "#766BCF", borderWidth: 7, pointBorderWidth: 1},
        topCurve: {backgroundColor: "#5BC5F2", borderColor: "#5BC5F2", borderDash: [5, 5], borderWidth: 1},
      };

      return {
        label: LABELS[key],
        data: value
          .split(",")
          .map(v => (v > 0 ? parseFloat(v) : 0))
          .slice(0, -1),
        ...(STYLES[key] || {}),
      };
    })
    .sort((a, b) => ORDER[a.label] - ORDER[b.label]);

  const xLabels = oestradiolData[0].data.map((_, index) => {
    if (index == 0 || !(index % 7)) {
      return index;
    } else {
      return "";
    }
  });

  const styles = {
    ...DEFAULT_LINE_CHART_STYLE,
    label: {
      y: data.results?.[0]?.oestradiol?.unit || "nmol/L",
    },
    legend: {
      position: "top",
    },
  };

  const dataset = {
    labels: xLabels,
    datasets: oestradiolData,
  };

  const options = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      customCanvasBackgroundColor: {
        color: styles.backgroundColor,
      },
      legend: {
        display: styles.legend.display,
        position: styles.legend.position ?? "bottom",
        labels: {
          boxWidth: styles.legend.boxWidth,
          boxHeight: styles.legend.boxHeight,
          padding: styles.legend.padding,
          font: {
            size: styles.legend.fontSize,
            weight: styles.legend.fontWeight,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
        backgroundColor: styles.legend.backgroundColor,
        color: styles.legend.color,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    tension: styles.tension,
    scales: {
      x: {
        display: styles.axis.xDisplay,
        backgroundColor: "#FFFFFF",
        title: {
          display: styles.label.xDisplay,
          text: styles.label.x,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min: 0,
          max: 31,
          stepSize: 7,
          z: 99999,
        },
      },
      y: {
        display: styles.axis.yDisplay,
        backgroundColor: styles.axis.backgroundColor,
        title: {
          display: styles.label.yDisplay,
          text: styles.label.y,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          scaleBeginAtZero: false,
          beginAtZero: false,
          min: 0,
          suggestedMin: 0,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          z: 99999,
        },
      },
    },
  };

  // Create the chart
  const newCanvas = document.createElement("canvas");
  newCanvas.setAttribute("id", "tempCanvas");
  document.body.appendChild(newCanvas);
  document.getElementById("tempCanvas").style.maxWidth = width + "px";
  document.getElementById("tempCanvas").style.maxHeight = height + "px";
  document.getElementById("tempCanvas").style.display = "none";

  const ctx = newCanvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  const newChart = new Chart(ctx, {
    type: "line",
    data: dataset,
    options: options,
  });

  const imageUrl = newChart.toBase64Image();
  newCanvas.remove();

  return imageUrl;
};

const getFshChartImage = (data, width, height) => {
  if (isEmpty(data?.curves?.fsh)) {
    throw new Error("No fsh data available for chart generation");
    return "";
  }

  if (width < 1 || height < 1) {
    throw new Error("Invalid width or height for chart generation");
    return "";
  }

  const fshData = Object.entries(data?.curves?.fsh || {})
    .map(([key, value]) => {
      const LABELS = {
        bottomCurve: "Low",
        meanCurve: "Average",
        personalCurve: "Personal",
        topCurve: "High",
      };

      const STYLES = {
        bottomCurve: {backgroundColor: "#EF94DD", borderColor: "#EF94DD", borderDash: [5, 5], borderWidth: 1},
        meanCurve: {backgroundColor: "#44b17a", borderColor: "#44b17a", borderWidth: 1},
        personalCurve: {backgroundColor: "#766BCF", borderColor: "#766BCF", borderWidth: 7, pointBorderWidth: 1},
        topCurve: {backgroundColor: "#5BC5F2", borderColor: "#5BC5F2", borderDash: [5, 5], borderWidth: 1},
      };

      return {
        label: LABELS[key],
        data: value
          .split(",")
          .map(v => (v > 0 ? parseFloat(v) : 0))
          .slice(0, -1),
        ...(STYLES[key] || {}),
      };
    })
    .sort((a, b) => ORDER[a.label] - ORDER[b.label]);

  const xLabels = fshData[0].data.map((_, index) => {
    if (index == 0 || !(index % 7)) {
      return index;
    } else {
      return "";
    }
  });

  const styles = {
    ...DEFAULT_LINE_CHART_STYLE,
    label: {
      y: data.results?.[0]?.fsh?.unit || "nmol/L",
    },
    legend: {
      position: "top",
    },
  };

  const dataset = {
    labels: xLabels,
    datasets: fshData,
  };

  const options = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      customCanvasBackgroundColor: {
        color: styles.backgroundColor,
      },
      legend: {
        display: styles.legend.display,
        position: styles.legend.position ?? "bottom",
        labels: {
          boxWidth: styles.legend.boxWidth,
          boxHeight: styles.legend.boxHeight,
          padding: styles.legend.padding,
          font: {
            size: styles.legend.fontSize,
            weight: styles.legend.fontWeight,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
        backgroundColor: styles.legend.backgroundColor,
        color: styles.legend.color,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    tension: styles.tension,
    scales: {
      x: {
        display: styles.axis.xDisplay,
        backgroundColor: "#FFFFFF",
        title: {
          display: styles.label.xDisplay,
          text: styles.label.x,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min: 0,
          max: 31,
          stepSize: 7,
          z: 99999,
        },
      },
      y: {
        display: styles.axis.yDisplay,
        backgroundColor: styles.axis.backgroundColor,
        title: {
          display: styles.label.yDisplay,
          text: styles.label.y,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          scaleBeginAtZero: false,
          beginAtZero: false,
          min: 0,
          suggestedMin: 0,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          z: 99999,
        },
      },
    },
  };

  // Create the chart
  const newCanvas = document.createElement("canvas");
  newCanvas.setAttribute("id", "tempCanvas");
  document.body.appendChild(newCanvas);
  document.getElementById("tempCanvas").style.maxWidth = width + "px";
  document.getElementById("tempCanvas").style.maxHeight = height + "px";
  document.getElementById("tempCanvas").style.display = "none";

  const ctx = newCanvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  const newChart = new Chart(ctx, {
    type: "line",
    data: dataset,
    options: options,
  });

  const imageUrl = newChart.toBase64Image();
  newCanvas.remove();

  return imageUrl;
};

const getLhChartImage = (data, width, height) => {
  if (isEmpty(data?.curves?.lh)) {
    throw new Error("No lh data available for chart generation");
    return "";
  }

  if (width < 1 || height < 1) {
    throw new Error("Invalid width or height for chart generation");
    return "";
  }

  const lhData = Object.entries(data?.curves?.lh || {})
    .map(([key, value]) => {
      const LABELS = {
        bottomCurve: "Low",
        meanCurve: "Average",
        personalCurve: "Personal",
        topCurve: "High",
      };

      const STYLES = {
        bottomCurve: {backgroundColor: "#EF94DD", borderColor: "#EF94DD", borderDash: [5, 5], borderWidth: 1},
        meanCurve: {backgroundColor: "#44b17a", borderColor: "#44b17a", borderWidth: 1},
        personalCurve: {backgroundColor: "#766BCF", borderColor: "#766BCF", borderWidth: 7, pointBorderWidth: 1},
        topCurve: {backgroundColor: "#5BC5F2", borderColor: "#5BC5F2", borderDash: [5, 5], borderWidth: 1},
      };

      return {
        label: LABELS[key],
        data: value
          .split(",")
          .map(v => (v > 0 ? parseFloat(v) : 0))
          .slice(0, -1),
        ...(STYLES[key] || {}),
      };
    })
    .sort((a, b) => ORDER[a.label] - ORDER[b.label]);

  const xLabels = lhData[0].data.map((_, index) => {
    if (index == 0 || !(index % 7)) {
      return index;
    } else {
      return "";
    }
  });

  const styles = {
    ...DEFAULT_LINE_CHART_STYLE,
    label: {
      y: data.results?.[0]?.lh?.unit || "nmol/L",
    },
    legend: {
      position: "top",
    },
  };

  const dataset = {
    labels: xLabels,
    datasets: lhData,
  };

  const options = {
    responsive: true,
    animation: false,
    maintainAspectRatio: false,
    plugins: {
      customCanvasBackgroundColor: {
        color: styles.backgroundColor,
      },
      legend: {
        display: styles.legend.display,
        position: styles.legend.position ?? "bottom",
        labels: {
          boxWidth: styles.legend.boxWidth,
          boxHeight: styles.legend.boxHeight,
          padding: styles.legend.padding,
          font: {
            size: styles.legend.fontSize,
            weight: styles.legend.fontWeight,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
        backgroundColor: styles.legend.backgroundColor,
        color: styles.legend.color,
      },
      title: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    tension: styles.tension,
    scales: {
      x: {
        display: styles.axis.xDisplay,
        backgroundColor: "#FFFFFF",
        title: {
          display: styles.label.xDisplay,
          text: styles.label.x,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          min: 0,
          max: 31,
          stepSize: 7,
          z: 99999,
        },
      },
      y: {
        display: styles.axis.yDisplay,
        backgroundColor: styles.axis.backgroundColor,
        title: {
          display: styles.label.yDisplay,
          text: styles.label.y,
          font: {
            size: styles.label.fontSize,
          },
          color: styles.label.color,
        },
        legend: {
          display: false,
        },
        grid: {
          display: false,
          borderColor: "#b3b3b3",
          drawBorder: true,
          drawTicks: true,
          tickColor: "#b3b3b3",
          z: 9999,
        },
        ticks: {
          scaleBeginAtZero: false,
          beginAtZero: false,
          min: 0,
          suggestedMin: 0,
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          z: 99999,
        },
      },
    },
  };

  // Create the chart
  const newCanvas = document.createElement("canvas");
  newCanvas.setAttribute("id", "tempCanvas");
  document.body.appendChild(newCanvas);
  document.getElementById("tempCanvas").style.maxWidth = width + "px";
  document.getElementById("tempCanvas").style.maxHeight = height + "px";
  document.getElementById("tempCanvas").style.display = "none";

  const ctx = newCanvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  const newChart = new Chart(ctx, {
    type: "line",
    data: dataset,
    options: options,
  });

  const imageUrl = newChart.toBase64Image();
  newCanvas.remove();

  return imageUrl;
};

const getFormScoreChartImage = (data, width, height) => {
  const score = Math.round(parseFloat(data.formScore || 0));
  const maxScore = 100;

  const styles = {
    ...DEFAULT_DOUGHNUT_CHART_STYLE,
    circumference: 320,
    cutout: "85%",
  };

  const dataset = [
    {
      data: [score, maxScore - score],
      backgroundColor: [styles.colorsRequired, styles.backgroundChartColor],
    },
  ];

  const paddingInPixels = 15;
  const chartPadding = paddingInPixels * 1.3; // padding on all sides

  const options = {
    circumference: styles.circumference,
    rotation: styles.rotation,
    animation: false,
    responsive: true,
    cutout: styles.cutout,
    borderColor: styles.borderColor,
    borderWidth: styles.borderWidth,
    hoverOffset: 0,
    layout: {
      padding: chartPadding,
    },
    plugins: {
      legend: false,
      tooltip: false,
      title: false,
      customCanvasBackgroundColor: {
        color: "#ffffff",
      },
    },
  };

  const centerTextPlugin = {
    id: "centerText",
    beforeDraw: chart => {
      const {ctx, width, height} = chart;

      ctx.save();

      const centerX = width / 2;
      const centerY = height / 2;
      const availableRadius = Math.min(width, height) / 2;

      // Draw the white circle background covering the entire available area
      const backgroundRadius = availableRadius - 5; // Small margin from edge
      const adjustedCenterY = centerY + 6;

      // Draw the white circle background
      ctx.beginPath();
      ctx.arc(centerX, adjustedCenterY, backgroundRadius, 0, 2 * Math.PI);
      ctx.fillStyle = "#ffffff";
      ctx.fill();

      // Draw the score text
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#333333";
      ctx.font = "128px Poppins";
      ctx.fillText(score.toString(), centerX, adjustedCenterY + 20);
      ctx.restore();
    },
  };

  const plugins = [centerTextPlugin];

  // Create the chart
  const newCanvas = document.createElement("canvas");
  newCanvas.setAttribute("id", "tempCanvas");
  document.body.appendChild(newCanvas);
  document.getElementById("tempCanvas").style.maxWidth = width + "px";
  document.getElementById("tempCanvas").style.maxHeight = height + "px";
  document.getElementById("tempCanvas").style.display = "none";

  const ctx = newCanvas.getContext("2d");
  ctx.canvas.width = width;
  ctx.canvas.height = height;

  const newChart = new Chart(ctx, {
    type: "doughnut",
    data: {
      datasets: dataset,
      labels: [],
    },
    options: options,
    plugins: plugins,
  });

  const imageUrl = newChart.toBase64Image();
  newCanvas.remove();

  return imageUrl;
};
